import React, { createContext, useEffect, useState } from 'react';

import { useAuthContext } from '../../hooks';
import { UserRanks } from '../../models/user';
import { getMyCoinsCountApi } from '../../services/user/user.api';
import { getMyNewNotificationsApi } from '../../services/notification/notification.api';
import { getMyRankApi } from '../../services/ranks/ranks.api';
import { STORAGE_KEYS } from '../../constants/storage-keys';

const PlatformContext = createContext<IPlatformContext>({
  loading: true,
});

export interface IPlatformContext {
  coinsCount?: number;
  notificationsCount?: number;
  getNotificationsCount?: () => void;
  getCoinsCount?: () => void;
  getUserRanks?: () => void;
  globalRank?: number;
  schoolRank?: number;
  globalRankIncrease?: number;
  schoolRankIncrease?: number;
  points?: number;
  loading: boolean;
}

interface PlatformProviderProps {
  children: React.ReactNode;
}

function PlatformProvider({ children }: PlatformProviderProps) {
  const [userRanksLoading, setUserRanksLoading] = useState(true);
  const [coinsCountLoading, setCoinsCountLoading] = useState(true);
  const [notificationsCountLoading, setNotificationsCountLoading] =
    useState(true);
  const [coinsCount, setCoinsCount] = useState<number>();
  const [notificationsCount, setNotificationsCount] = useState<number>();
  const [userRanks, setUserRanks] = useState<UserRanks>();
  const { isAuthenticated, isSubscribed } = useAuthContext();

  async function getCoinsCount() {
    setCoinsCountLoading(true);
    try {
      const coinsCount = await getMyCoinsCountApi();
      setCoinsCount(coinsCount);
    } catch (error) {
      console.error(error);
    } finally {
      setCoinsCountLoading(false);
    }
  }

  async function getNotificationsCount() {
    setNotificationsCountLoading(true);
    try {
      const res = await getMyNewNotificationsApi({
        page: 1,
        pageSize: 0,
      });
      setNotificationsCount(res?.data.newNotificationsCount);
    } catch (error) {
      console.error(error);
    } finally {
      setNotificationsCountLoading(false);
    }
  }

  async function getUserRanks() {
    setUserRanksLoading(true);
    try {
      const ranks = await getMyRankApi();
      setUserRanks({
        globalRank: ranks?.globalRank || 0,
        globalRankIncrease: ranks?.previousGlobalRank || 0,
        schoolRank: ranks?.schoolRank || 0,
        schoolRankIncrease: ranks?.previousSchoolRank || 0,
        totalPoints: ranks?.points?.totalPoint || 0,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setUserRanksLoading(false);
    }
  }

  useEffect(() => {
    let notificationInterval: NodeJS.Timer | undefined;
    let subscriptionInterval: NodeJS.Timer | undefined;
    let coinInterval: NodeJS.Timer | undefined;
    if (typeof window !== 'undefined') {
      const accstkn = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (isAuthenticated() && accstkn) {
        const NOTIFICATION_TIME = 1; // 1min
        notificationInterval = setInterval(() => {
          getNotificationsCount();
        }, NOTIFICATION_TIME * 1000 * 60);

        const SUBSCRIPTION_TIME = 1000 * 60 * 60 * 24; // 1day
        subscriptionInterval = setInterval(() => {
          isSubscribed();
        }, SUBSCRIPTION_TIME);

        const COIN_TIME = 5; // 5mins
        coinInterval = setInterval(() => {
          getCoinsCount();
        }, COIN_TIME * 1000 * 60);
      }
    }
    return () => {
      clearInterval(notificationInterval);
      clearInterval(subscriptionInterval);
      clearInterval(coinInterval);
    };
    //eslint-disable-next-line
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const accstkn = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (isAuthenticated() && accstkn) {
        isSubscribed();
        getCoinsCount();
        getNotificationsCount();
        getUserRanks();
      }
    }
  }, [isAuthenticated]);

  return (
    <PlatformContext.Provider
      value={{
        coinsCount,
        notificationsCount,
        getNotificationsCount,
        globalRank: userRanks?.globalRank,
        schoolRank: userRanks?.schoolRank,
        globalRankIncrease: userRanks?.globalRankIncrease,
        schoolRankIncrease: userRanks?.schoolRankIncrease,
        getCoinsCount,
        points: userRanks?.totalPoints,
        getUserRanks,
        loading:
          coinsCountLoading || notificationsCountLoading || userRanksLoading,
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

export { PlatformContext, PlatformProvider };
